import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Cookies from 'js-cookie';

import { User } from '@restworld/utility-types';
import { RadioGroup } from '@restworld/ui-ds-base';
import { useNavigationContext } from '@restworld/utils-common';

import { isLanguageChangeModalVisibleAtom } from '.';
import { ModalToggleWrapper } from './form-modal-wrapper';
import { useV2Router } from '../dashboard/hooks/use-v2-router';

const LANGUAGE_OPTIONS = {
  EN: 'en',
  IT: 'it',
} as const;
const DEFAULT_LANGUAGE = LANGUAGE_OPTIONS.IT;

export const LanguageChangeModal = () => {
  const { t } = useTranslation('user-settings');
  const [modalVisible, modalVisibleToggler] = useAtom(isLanguageChangeModalVisibleAtom);
  const languageOptions = useLanguageOptionsList();
  const methods = useForm<LanguageFormValuesType>();

  // const { router } = useNavigationContext();
  const router = useV2Router();
  const locale = useCurrentLocale();
  const basePath = router.asPath;

  const formItems = React.useMemo(() => {
    return (
      <>
        <Controller
          name="language"
          control={methods.control}
          rules={{ required: true }}
          defaultValue={locale}
          render={({ field }) => (
            <RadioGroup options={languageOptions} id="languages" orientation="horizontal" {...field} />
          )}
        />
      </>
    );
  }, [languageOptions, locale, methods.control]);

  const onSubmit: SubmitHandler<LanguageFormValuesType> = React.useCallback(
    ({ language }) => {
      Cookies.set('NEXT_LOCALE', language, { expires: 365 * 24 * 60 * 60 });
      router.push(basePath, basePath, { locale: language });
      modalVisibleToggler(false);
    },
    [basePath, modalVisibleToggler, router]
  );

  return (
    <ModalToggleWrapper
      onSubmitHandler={methods.handleSubmit(onSubmit)}
      onCancelHandler={() => modalVisibleToggler(false)}
      modalVisible={modalVisible}
      onModalClose={() => modalVisibleToggler(false)}
      title={t('choose_preferred_language')}
    >
      {formItems}
    </ModalToggleWrapper>
  );
};

type LanguageFormValuesType = User & { language: string };

export function useLanguageOptionsList() {
  const { t } = useTranslation('user-settings');

  const languageOptions = React.useMemo(
    () => [
      { id: LANGUAGE_OPTIONS.IT, key: LANGUAGE_OPTIONS.IT, label: t('italian') },
      { id: LANGUAGE_OPTIONS.EN, key: LANGUAGE_OPTIONS.EN, label: t('english') },
    ],
    [t]
  );

  return languageOptions;
}

export function useCurrentLocale() {
  const { router } = useNavigationContext();
  const locale: string = router.locale || DEFAULT_LANGUAGE;

  return locale;
}
